import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CheckCircleIcon from '../../assets/icons/CheckCircleIcon';
import IncreaseIcon from '../../assets/icons/IncreaseIcon';
import DecreaseIcon from '../../assets/icons/DecreaseIcon';
import DeleteCircleIcon from '../../assets/icons/DeleteCircleIcon';

import { defaultRankingAndActivity, leadActivityCategories } from '../../constants/sales';

const ActiveSurgeonCard = (props) => {
  const {
    id,
    lead,
    surgeons = [],
    specialities = [],
    cases = [],
    onClick,
    tenantColor,
    isOverlay,
    caseVolumeMin,
    caseVolumeMax,
    surgeonRanking,
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging && !isOverlay ? 0 : 1,
    zIndex: isDragging && !isOverlay ? 0 : 1,
  };

  const getSurgeonName = useCallback((surgeonId) => {
    const surgeon = surgeons.find((s) => s.id === surgeonId);
    return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : '';
  }, [surgeons]);

  const getSurgeonSpeciality = useCallback((sId) => {
    const speciality = specialities.find((s) => s.id === sId);
    return speciality?.name || '';
  }, [specialities]);

  const rankingScore = useMemo(() => {
    if (!lead) {
      return '';
    }

    const { ranking } = lead;

    if (!ranking) {
      return;
    }

    const { teaching, kolStatus, published, training } = ranking;
    const sum = teaching + kolStatus + published + training;
    const _ranking = surgeonRanking || defaultRankingAndActivity;

    if (sum && sum <= 20 && _ranking) {
      return _ranking[sum].rankCategory || 'E';
    }

    return '';
  }, [lead, surgeonRanking]);

  const getActivityIcon = useCallback(() => {
    if (!lead) {
      return null;
    }

    const { ranking } = lead;
    const { teaching, kolStatus, published, training } = ranking;
    const sum = teaching + kolStatus + published + training;
    const _ranking = surgeonRanking || defaultRankingAndActivity;
    const score = _ranking[sum];

    const activityTarget = score?.activityTarget || 0;
    const dateStart = moment().subtract(1, 'month').startOf('month');
    const dateEnd = moment().subtract(1, 'month').endOf('month');

    const bookingActivity = lead?.activity?.filter((a) => [leadActivityCategories.TRAINING.value, leadActivityCategories.CALL.value, leadActivityCategories.MEETING.value, leadActivityCategories.DEMO.value].includes(a?.category));
    const stageActivity = lead?.activity?.filter((a) => a.type === 'LEAD_STAGE_UPDATED' && dateStart.isBefore(a.createdAt) && dateEnd.isAfter(a.createdAt))
    const lastMonthActivity = bookingActivity?.filter((a) => dateStart.isBefore(a.bookingData?.date) && dateEnd.isAfter(a.bookingData?.date));
    const activityNumber = stageActivity?.length + lastMonthActivity?.length;

    if (activityNumber >= activityTarget) {
      return <CheckCircleIcon color='#81B214' />;
    } else {
      return <DeleteCircleIcon color='#F53434' />;
    }
  }, [lead, surgeonRanking]);

  const numberOfCases = useMemo(() => {
    const dateStart = moment().subtract(1, 'month').startOf('month');
    const dateEnd = moment().subtract(1, 'month').endOf('month');

    if (!cases || !lead) {
      return 0;
    }

    return cases?.filter((caseItem) => {
      return caseItem.surgeon === lead.surgeon && dateStart.isBefore(caseItem.date) && dateEnd.isAfter(caseItem.date);
    })?.length || 0;
  }, [lead, cases]);

  const getClassName = useCallback(() => {
    if (numberOfCases < caseVolumeMin) {
      return ' lead-card__rotten';
    }
    if (numberOfCases > caseVolumeMax) {
      return ' lead-card__success';
    }
    return '';
  }, [numberOfCases, caseVolumeMin, caseVolumeMax]);

  const getColor = useCallback(() => {
    if (numberOfCases < caseVolumeMin) {
      return '#F53434';
    }
    if (numberOfCases > caseVolumeMax) {
      return '#81B214';
    }
    return 'inherit';
  }, [numberOfCases, caseVolumeMin, caseVolumeMax]);

  const getIcon = useCallback(() => {
    if (numberOfCases < caseVolumeMin) {
      return <div style={{ marginLeft: 4 }}><DecreaseIcon color='#F53434' /></div>;
    }
    if (numberOfCases > caseVolumeMax) {
      return <div style={{ marginLeft: 4 }}><IncreaseIcon color='#81B214' /></div>;
    }
    return null;
  }, [numberOfCases, caseVolumeMin, caseVolumeMax]);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ ...style, '--colorHover': tenantColor }}
      className={`active-surgeon-card lead-card${getClassName()}`}
      onClick={onClick}
    >
      <div className='font-bold'>{getSurgeonName(lead.surgeon)}</div>
      <div className='m-t-sm secondary'>{getSurgeonSpeciality(lead.surgeonDetails?.speciality)}</div>
      <div className='d-flex space-between' style={{ marginTop: 12 }}>
        <div className='d-flex direction-column align-start' style={{ width: 60 }}>
          <div>Cases</div>
          <div className='m-t-sm d-flex'>
            <div className='font-bold font-size-bg' style={{ color: getColor(), paddingLeft: 12, marginBottom: 2 }}>{numberOfCases}</div>
            {getIcon()}
          </div>
        </div>
        <div className='d-flex direction-column' style={{ width: 60 }}>
          <div>Activity</div>
          <div className='m-t-sm'>{getActivityIcon()}</div>
        </div>
        <div className='d-flex direction-column' style={{ width: 60 }}>
          <div>Rank</div>
          <div className='m-t-sm font-bold font-size-bg'>{rankingScore}</div>
        </div>
      </div>
    </div>
  );
};

export default ActiveSurgeonCard;
